/**
 * Types mirroring the API contract.
 *
 * These are written by hand today. Once the backend publishes its OpenAPI
 * specification (BE-28) they are generated from it instead: the two
 * repositories are separate, so nothing else catches a contract change —
 * a mismatched field is a runtime bug, not a compile error.
 */

/** Error codes the backend can return. English: this is a machine contract. */
export const ApiErrorCode = {
  InvalidRequest: 'INVALID_REQUEST',
  Unauthorized: 'UNAUTHORIZED',
  Forbidden: 'FORBIDDEN',
  NotFound: 'NOT_FOUND',
  Conflict: 'CONFLICT',
  Unprocessable: 'UNPROCESSABLE_ENTITY',
  Internal: 'INTERNAL_ERROR',
  SnapshotNotReady: 'SNAPSHOT_NOT_READY',

  /**
   * Codes of the auth/RBAC layer (lowercase, backend/internal/auth). The
   * backend's older error paths still use the uppercase ones above.
   */
  AuthUnauthorized: 'unauthorized',
  AuthForbidden: 'forbidden',
  OutOfScope: 'out_of_scope',
  UserNotRegistered: 'user_not_registered',
  UserInactive: 'user_inactive',
  AuthConflict: 'conflict',
  Validation: 'validation',
  AuthNotFound: 'not_found',
  Unavailable: 'unavailable',

  /** Client-side only: the request never reached the server. */
  Network: 'NETWORK_ERROR',
  /** Client-side only: the request exceeded the timeout. */
  Timeout: 'TIMEOUT',
} as const

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode]

/** Shape of the body the backend sends on failure. */
export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
  }
}

/** GET /health */
export interface HealthResponse {
  status: string
  uptime: string
  timestamp: string
}

/** A property (plaza) in `/me.properties`: no business unit travels to the frontend. */
export interface PropertyInfo {
  code: string
  name: string
  location: string
  /** The viewer only offers properties with a published plan. */
  hasPublishedPlan: boolean
}

/** GET /api/v1/me (backend/CONTRATO-AUTH-RBAC.md). */
export interface MeResponse {
  id: string
  email: string
  displayName: string
  role: { name: string }
  permissions: string[]
  allProperties: boolean
  properties: PropertyInfo[]
  authMode: 'entra' | 'dev-bypass' | 'entra-no-db'
}
